import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate } from 'react-router-dom';
import { getFaqs } from '../services/faqService';
import type { FaqItem } from '../services/faqService';

let faqsRequest: Promise<FaqItem[]> | null = null;

const ALLOWED_TAGS = new Set(['a', 'br', 'p', 'div', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'span']);
const ALLOWED_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizePlainTextAnswer(value: string): string {
  return escapeHtml(value).replace(/\r\n/g, '\n').replace(/\n/g, '<br />');
}

function sanitizeFaqHtml(rawAnswer: string): string {
  if (!rawAnswer) return '';

  const value = rawAnswer.trim();
  if (!/<[a-z][\s\S]*>/i.test(value)) {
    return normalizePlainTextAnswer(value);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(value, 'text/html');

  const sanitizeNode = (node: Node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tagName)) {
      const parent = element.parentNode;
      if (parent) {
        while (element.firstChild) {
          parent.insertBefore(element.firstChild, element);
        }
        parent.removeChild(element);
      }
      return;
    }

    if (tagName === 'a') {
      const hrefValue = element.getAttribute('href')?.trim() ?? '';

      let safeHref = '';
      if (hrefValue) {
        try {
          const parsedUrl = new URL(hrefValue, window.location.origin);
          if (ALLOWED_LINK_PROTOCOLS.includes(parsedUrl.protocol)) {
            safeHref = hrefValue;
          }
        } catch {
          safeHref = '';
        }
      }

      element.setAttribute('href', safeHref || '#');
      element.setAttribute('target', '_blank');
      element.setAttribute('rel', 'noopener noreferrer');

      Array.from(element.attributes).forEach((attr) => {
        if (!['href', 'target', 'rel'].includes(attr.name.toLowerCase())) {
          element.removeAttribute(attr.name);
        }
      });
    } else {
      Array.from(element.attributes).forEach((attr) => {
        element.removeAttribute(attr.name);
      });
    }

    Array.from(element.childNodes).forEach(sanitizeNode);
  };

  Array.from(doc.body.childNodes).forEach(sanitizeNode);
  return doc.body.innerHTML;
}

const FAQ: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const memoizedFaqsRequest = useMemo(() => {
    if (!faqsRequest) {
      faqsRequest = getFaqs();
    }
    return faqsRequest;
  }, []);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await memoizedFaqsRequest;
        setFaqs(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load FAQs');
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, [memoizedFaqsRequest]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 8 }, py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent', px: { xs: 2, md: 8 }, py: 6 }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: '#fff', mb: 2, ml: 0, p: 1, display: 'block' }}>
          <ArrowBackIosNewIcon sx={{ fontSize: 28 }} />
        </IconButton>
        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, mb: 4, mt: 2, fontSize: { xs: 22, md: 28 }, textAlign: 'left' }}>
          FAQs
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {faqs.length === 0 ? (
            <Typography sx={{ color: 'text.secondary' }}>No FAQs available.</Typography>
          ) : (
            faqs.map((faq, idx) => (
              <Accordion
                key={faq.id}
                sx={{
                  background: theme.palette.mode === 'dark' ? '#232228' : '#f5f5f5',
                  color: theme.palette.text.primary,
                  borderRadius: 3,
                  boxShadow: 'none',
                  mb: 1,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { borderRadius: 3 },
                  textAlign: 'left',
                }}
                disableGutters
                defaultExpanded={idx === 0}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.text.secondary, fontSize: 32 }} />}
                  sx={{
                    fontWeight: 600,
                    fontSize: 18,
                    color: theme.palette.text.primary,
                    borderRadius: 3,
                    minHeight: 64,
                    '& .MuiAccordionSummary-content': { my: 1 },
                    textAlign: 'left',
                  }}
                >
                  {faq.question}
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    fontSize: 16,
                    color: 'text.secondary',
                    borderTop: `1px solid ${theme.palette.divider}`,
                    textAlign: 'left',
                    '& a': {
                      color: 'inherit',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                    },
                  }}
                >
                  <span dangerouslySetInnerHTML={{ __html: sanitizeFaqHtml(faq.answer) }} />
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FAQ;
